import { useContext } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import {
  Form, FieldSet, Button, Typography, ColumnLayout, Column, ModalBody,
} from '@visionplanner/ui-react-material';
import { SalesOnBoarding } from '../sales.context';
import { onBoardingInformationFormSchema } from './sales-form-validation';
import { saveOnBoardingInformation } from '../sales-onboarding-partner.request';
import { GENDER_OPTIONS } from '../../../common/enums';
import { types } from '../sales-onboarding-partner.reducer';
import { useAsyncCallback } from '../../../common/custom-hooks/use-async-callback';
import { StyledModalFooter } from '../../styles';

/** Component to create or edit partner onboarding information details */
const OnBoardingInformation = () => {
  const {
    setActiveStep,
    activeStep,
    dispatch,
    state,
  } = useContext(SalesOnBoarding);
  const { partnerId, onboardingInformation } = state;

  const handleSubmit = useAsyncCallback(async (formValues, { setSubmitting }) => {
    try {
      if (!isEqual(onboardingInformation, formValues)) {
        await saveOnBoardingInformation(partnerId, formValues);
        dispatch({
          type: types.updateOnBoardingInformation,
          payload: { formValues },
        });
      }
      setActiveStep(activeStep + 1);
    } finally {
      setSubmitting(false);
    }
  }, [activeStep, dispatch, onboardingInformation, partnerId, setActiveStep]);

  return (
    <>
      <Form
        initialValues={onboardingInformation}
        validateFormSchema={onBoardingInformationFormSchema}
        onSubmit={handleSubmit}
        isInitiallyValid={onBoardingInformationFormSchema.isValidSync(onboardingInformation)}
        dataTa="onboarding-information-form"
        className="full-height flex-column"
      >
        {
          ({
            isValid, isSubmitting,
          }) => (
            <>
              <ModalBody className="pos-rel">
                <Typography use="h6">Contactpersoon/eerste gebruiker</Typography>
                <ColumnLayout>
                  <Column span={4}>
                    <FieldSet
                      type="text"
                      name="firstContactPersonFirstName"
                      required
                      label="Voornaam"
                      dataTa="first-name"
                    />
                  </Column>
                  <Column span={4}>
                    <FieldSet
                      type="text"
                      name="firstContactPersonMiddleName"
                      label="Tussenvoegsel"
                      dataTa="middle-name"
                    />
                  </Column>
                  <Column span={4}>
                    <FieldSet
                      type="text"
                      name="firstContactPersonLastName"
                      label="Achternaam"
                      required
                      dataTa="last-name"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={3}>
                    <FieldSet
                      type="selectOne"
                      controlType="autocomplete"
                      label="Geslacht"
                      name="gender"
                      options={GENDER_OPTIONS}
                      placeholder="Selecteren"
                      required
                      dataTa="type-of-gender"
                      noOptionsMessage="Geen opties"
                      className="type-of-gender"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={9}>
                    <FieldSet
                      type="email"
                      label="E-mailadres"
                      name="firstContactPersonEmail"
                      required
                      dataTa="email"
                    />
                  </Column>
                </ColumnLayout>
              </ModalBody>
              <StyledModalFooter>
                <Button buttonType="secondary" dataTa="go-to-general-information" onClick={() => setActiveStep(activeStep - 1)}>Vorige</Button>
                <Button type="submit" dataTa="go-to-invoicing-information" disabled={!isValid || isSubmitting}>Volgende</Button>
              </StyledModalFooter>
            </>
          )
        }
      </Form>
    </>
  );
};

export default OnBoardingInformation;

OnBoardingInformation.propTypes = {
  /** function to set current or active step */
  setActiveStep: PropTypes.func,
  /** current step */
  activeStep: PropTypes.number,
};
